import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useGetTrackByFrameSeqQuery, useUpdateTrackIDMutation } from '@/hook';
import {
  Close,
  VButton,
  VFlex,
  VInput,
  VModal,
  VStack,
  useFlag,
} from '@/package';
import {
  useAuthStore,
  useBoxClipboardStore,
  useLabelingBoxStore,
  useTaskFrameStore,
  useToolMenuStore,
} from '@/store';
import type { BoxWithLabel, FrameData, TrackApi } from '@/type';

import { LinkedFrameList } from './LinkedFrameList';
import { TrackIDChangeAlertSnackBar } from './TrackIDChangeSnackBar';
import type { LinkedFrameItemData } from './type';
import { isAssignedFrame } from './util';

export const EditLinkedTrackIDModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutateAsync: updateTrackID } = useUpdateTrackIDMutation();
  const user = useAuthStore(state => state.user);
  const currentFrame = useTaskFrameStore(state => state.currentFrame);
  const toolMap = useToolMenuStore(state => state.toolMap);
  const pcdIndexBoxListMap = useLabelingBoxStore(
    state => state.pcdIndexBoxListMap,
  );
  const selectedBoxIdMap = useLabelingBoxStore(state => state.selectedBoxIdMap);
  const copiedTrackID = useBoxClipboardStore(
    state => state.copiedTrackID ?? '',
  );
  const { value: visible, setFalse: hide, setTrue: show } = useFlag(false);
  const [excludedFrameSeqSet, setExcludedFrameSeqSet] = useState<Set<number>>(
    new Set(),
  );
  const boxList = useMemo(
    () => useLabelingBoxStore.getState().getCurrentBoxList(),
    [pcdIndexBoxListMap],
  );
  const selectedBox = useMemo(
    () =>
      useLabelingBoxStore
        .getState()
        .getCurrentSelectedBoxList()[0] as Nullish<BoxWithLabel>,
    [selectedBoxIdMap],
  );
  const { data: frameList } = useGetTrackByFrameSeqQuery({
    taskID: currentFrame?.taskID,
    trackID: selectedBox?.trackId,
  });
  const { formState, register, getValues, setValue } = useForm({
    defaultValues: {
      copiedTrackID,
      currentTrackID: selectedBox?.trackId ?? '',
    },
  });
  const linkedFrameListGroup = useMemo(
    () =>
      frameList?.reduce((acc, cur, index, arr) => {
        const prev = arr[index - 1];

        if (!prev || cur.frameSeq !== prev.frameSeq + 1) {
          acc.push([cur]);
        } else {
          acc[acc.length - 1].push(cur);
        }

        return acc;
      }, [] as LinkedFrameItemData[][]) ?? [],
    [frameList],
  );
  const totalFrameCount = useMemo(
    () =>
      frameList?.filter(frame => !excludedFrameSeqSet.has(frame.frameSeq))
        .length ?? 0,
    [excludedFrameSeqSet, frameList],
  );
  const hasNotAssignedFrame = useMemo(() => {
    return frameList?.some(frame => !isAssignedFrame(frame, user));
  }, [frameList, user]);

  const handleApply = (
    frameList?: Pick<
      FrameData,
      'frameSeq' | 'reviewerID' | 'taskID' | 'workerID' | 'workState'
    >[],
  ) => {
    if (
      formState.isValid &&
      currentFrame &&
      selectedBox?.trackId &&
      copiedTrackID
    ) {
      const param = {
        taskID: currentFrame.taskID,
        trackID: selectedBox.trackId,
        newTrackID: copiedTrackID,
      } as TrackApi.UpdateTrackID.Request;

      if (frameList) {
        param.frameSeqRange = [
          `${frameList[0].frameSeq}`,
          `${frameList[frameList.length - 1].frameSeq}`,
        ];
      }

      return updateTrackID(param).then(() => {
        useLabelingBoxStore
          .getState()
          .changeBoxTrackID(selectedBox.id, copiedTrackID);
        hide();
      });
    } else {
      return Promise.resolve();
    }
  };

  const handleNavigateToFrame = (
    frameItem: Pick<
      FrameData,
      'frameSeq' | 'reviewerID' | 'taskID' | 'workerID' | 'workState'
    >,
  ) => {
    navigate(
      `/labeling/${currentFrame?.projectID}/${frameItem.taskID}/${frameItem.frameSeq}`,
    );
    hide();
  };

  const handleRemoveItem = (frameItem: LinkedFrameItemData) => {
    setExcludedFrameSeqSet(prev => new Set([...prev, frameItem.frameSeq]));
  };

  const handleRemoveList = (frameList: LinkedFrameItemData[]) => {
    setExcludedFrameSeqSet(prev => {
      const newSet = new Set(prev);

      frameList.forEach(frame => newSet.add(frame.frameSeq));

      return newSet;
    });
  };

  useEffect(() => {
    if (useToolMenuStore.getState().isSelectedByToolName('updateTrackID')) {
      show();
    }
  }, [toolMap]);

  useEffect(() => {
    if (!visible) {
      setExcludedFrameSeqSet(new Set());
    }
  }, [visible]);

  useEffect(() => {
    setValue('copiedTrackID', copiedTrackID, {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue('currentTrackID', selectedBox?.trackId ?? '', {
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [copiedTrackID, selectedBox]);

  return (
    <VModal
      footer={() => {
        return (
          <VFlex items="center" justify="end">
            <VFlex gap={0.25}>
              <VButton
                outlined
                size="md"
                variant="secondary"
                onClick={() => hide()}
              >
                {t('common.cancel.isocinchonine')}
              </VButton>
              <VButton
                disabled={!formState.isValid}
                size="md"
                variant="primary"
                onClick={() =>
                  Promise.all(linkedFrameListGroup.map(handleApply))
                }
              >
                {t(
                  'labeling.editLinkedTrackIDModalApplyAllButtonText.repercussiveness',
                )}
              </VButton>
            </VFlex>
          </VFlex>
        );
      }}
      size="small"
      visible={visible}
      onUpdateVisible={v => (v ? show() : hide())}
    >
      <VStack className="size-full bg-white" gap={0}>
        <VFlex items="center" justify="between" gap={0.625}>
          <span className="text-body-xl text-black">
            {t('labeling.editLinkedTrackIDModalTitle.bibliography')}
          </span>
          <Close
            className="size-6 shrink-0 cursor-pointer text-gray-400 hover:text-gray-600"
            role="button"
            onClick={() => hide()}
          />
        </VFlex>

        <VStack className="flex-1 py-4" gap={0.5}>
          <VStack gap={0.25}>
            <span className="text-body-xl text-black">
              {t('labeling.editLinkedTrackIDModalEditTitle.graptolite')}
            </span>
            <span className="text-body-lg whitespace-pre-line text-gray-600">
              {t('labeling.editLinkedTrackIDModalEditMessage.lighthouseman')}
            </span>
          </VStack>

          <VStack gap={0.5}>
            <VInput
              comment={formState.errors.currentTrackID?.message}
              fluid
              inputClassName="text-right"
              label={t(
                'labeling.editLinkedTrackIDModalCurrentTrackID.linnaeite',
              )}
              placeholder="-"
              orientation="horizontal"
              readOnly
              size="md"
              valid={formState.errors.currentTrackID == null}
              {...register('currentTrackID', {
                required: {
                  value: true,
                  message: t(
                    'labeling.editLinkedTrackIDModalTrackIDNotFoundMessage.lipopexia',
                  ),
                },
                pattern: {
                  value: /^[0-9]{19}$/,
                  message: t(
                    'labeling.editLinkedTrackIDModalTrackIDInvalidateMessage.liveryless',
                  ),
                },
              })}
            />
            <VInput
              comment={formState.errors.copiedTrackID?.message}
              fluid
              inputClassName="text-right"
              label={t(
                'labeling.editLinkedTrackIDModalCopiedTrackID.logograph',
              )}
              placeholder="-"
              orientation="horizontal"
              readOnly
              size="md"
              valid={formState.errors.copiedTrackID == null}
              {...register('copiedTrackID', {
                required: {
                  value: true,
                  message: t(
                    'labeling.editLinkedTrackIDModalNoCopiedMessage.loricati',
                  ),
                },
                pattern: {
                  value: /^[0-9]{19}$/,
                  message: t(
                    'labeling.editLinkedTrackIDModalTrackIDInvalidateMessage.liveryless',
                  ),
                },
                validate: {
                  isSameAsCurrentTrackID: (value: string) => {
                    return value === getValues('currentTrackID')
                      ? t(
                          'labeling.editLinkedTrackIDModalSameTrackIDMessage.lucidity',
                        )
                      : true;
                  },
                  isSameTrackIDInThisFrame: (value: string) => {
                    return boxList.some(box => box.trackId === value)
                      ? t(
                          'labeling.editLinkedTrackIDModalAlreadyTrackIDMessage.lieproof',
                        )
                      : true;
                  },
                },
              })}
            />
          </VStack>

          <VStack className="flex-1" gap={0.5}>
            <VFlex justify="between" items="center" gap={0.25}>
              <span className="text-body-lg font-bold text-gray-600">
                {t('labeling.editLinkedTrackIDModalLinkedFrameList.lumination')}
              </span>
              <span className="text-body-md text-black">
                All {totalFrameCount}
              </span>
            </VFlex>

            <VStack className="max-h-[336px] min-h-[336px] flex-1" gap={0.25}>
              <VStack className="flex-1 overflow-y-auto pr-1" gap={0.25}>
                {linkedFrameListGroup.map(frameList => {
                  return (
                    <LinkedFrameList
                      key={`${frameList[0].frameSeq}-${frameList[frameList.length - 1].frameSeq}`}
                      excludedFrameSeqSet={excludedFrameSeqSet}
                      frameList={frameList}
                      onApply={handleApply}
                      onNavigate={handleNavigateToFrame}
                      onRemoveItem={handleRemoveItem}
                      onRemoveList={handleRemoveList}
                    />
                  );
                })}
              </VStack>

              {hasNotAssignedFrame && (
                <TrackIDChangeAlertSnackBar
                  message={
                    t(
                      'labeling.editLinkedTrackIDModalWarningSnackBarMessage.corkish',
                    ) ?? ''
                  }
                />
              )}
            </VStack>
          </VStack>
        </VStack>
      </VStack>
    </VModal>
  );
};
